import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { AttentionStore, isTabActive, type AttentionEvent } from '../utils/attentionStore'

const AttentionContext = createContext<AttentionStore | null>(null)
const EMPTY_PENDING: readonly AttentionEvent[] = Object.freeze([])
const noopSubscribe = (_listener: () => void) => () => {}
const emptySnapshot = () => EMPTY_PENDING

export function AttentionProvider({ children, store: suppliedStore }: { children: ReactNode; store?: AttentionStore }): JSX.Element {
  const [store] = useState(() => suppliedStore ?? new AttentionStore())

  useEffect(() => {
    const acknowledgeWhenActive = () => {
      if (isTabActive()) store.acknowledge()
    }
    window.addEventListener('focus', acknowledgeWhenActive)
    document.addEventListener('visibilitychange', acknowledgeWhenActive)
    return () => {
      window.removeEventListener('focus', acknowledgeWhenActive)
      document.removeEventListener('visibilitychange', acknowledgeWhenActive)
    }
  }, [store])

  return <AttentionContext.Provider value={store}>{children}</AttentionContext.Provider>
}

/** Drops notifications after the component that called this hook unmounts; no screen can receive them. */
export function useAttentionNotify(): (event: AttentionEvent) => void {
  const store = useContext(AttentionContext)
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  return useCallback((event: AttentionEvent) => {
    if (store && mounted.current) store.notify(event, isTabActive())
  }, [store])
}

export function useAttentionPending(): readonly AttentionEvent[] {
  const store = useContext(AttentionContext)
  return useSyncExternalStore(store?.subscribe ?? noopSubscribe, store?.getPending ?? emptySnapshot)
}

export function useAttentionAcknowledge(): () => void {
  const store = useContext(AttentionContext)
  return useCallback(() => store?.acknowledge(), [store])
}
